import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import PagesLayout from 'layout/Pages';
import SimpleLayout from 'layout/Simple';
import PermissionGuard from 'utils/route-guard/PermissionGuard';

import { SimpleLayoutType } from 'config';

// render - dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));
const DashboardAnalytics = Loadable(lazy(() => import('pages/dashboard/analytics')));

// render - widget
const WidgetStatistics = Loadable(lazy(() => import('pages/widget/statistics')));
const WidgetData = Loadable(lazy(() => import('pages/widget/data')));
const WidgetChart = Loadable(lazy(() => import('pages/widget/chart')));

// render - applications
const UserProfile = Loadable(lazy(() => import('pages/apps/profiles/user')));
const UserTabPersonal = Loadable(lazy(() => import('sections/apps/profiles/user/TabPersonal')));
const UserTabPayment = Loadable(lazy(() => import('sections/apps/profiles/user/TabPayment')));
const UserTabPassword = Loadable(lazy(() => import('sections/apps/profiles/user/TabPassword')));
const UserTabSettings = Loadable(lazy(() => import('sections/apps/profiles/user/TabSettings')));

const AccountProfile = Loadable(lazy(() => import('pages/apps/profiles/account')));
const AccountTabProfile = Loadable(lazy(() => import('sections/apps/profiles/account/TabProfile')));
const AccountTabPersonal = Loadable(lazy(() => import('sections/apps/profiles/account/TabPersonal')));
const AccountTabAccount = Loadable(lazy(() => import('sections/apps/profiles/account/TabAccount')));
const AccountTabPassword = Loadable(lazy(() => import('sections/apps/profiles/account/TabPassword')));
const AccountTabRole = Loadable(lazy(() => import('sections/apps/profiles/account/TabRole')));
const AccountTabSettings = Loadable(lazy(() => import('sections/apps/profiles/account/TabSettings')));

const AppEquipmentList = Loadable(lazy(() => import('pages/apps/equipment/list')));
const AppEquipmentCreate = Loadable(lazy(() => import('pages/apps/equipment/create')));
const AppEquipmentDetails = Loadable(lazy(() => import('pages/apps/equipment/details')));
const AppEquipmentEdit = Loadable(lazy(() => import('pages/apps/equipment/edit')));
const AppWarehouseList = Loadable(lazy(() => import('pages/apps/warehouse/list')));
const AppAdminWarehouseList = Loadable(lazy(() => import('pages/apps/warehouse/admin-list')));
const AppOwnInventoryList = Loadable(lazy(() => import('pages/apps/own-inventory/list')));
const AppRentedInventoryList = Loadable(lazy(() => import('pages/apps/rented-inventory/list')));

const AppEquipmentOrderCreate = Loadable(lazy(() => import('pages/apps/equipment-order/create')));
const AppEquipmentOrderEdit = Loadable(lazy(() => import('pages/apps/equipment-order/edit')));
const AppEquipmentOrderList = Loadable(lazy(() => import('pages/apps/equipment-order/list')));
const AppOrderItemsCreate = Loadable(lazy(() => import('pages/apps/order-item/create')));
const AppOrderItemEdit = Loadable(lazy(() => import('pages/apps/order-item/edit')));
const AppOrderItemsList = Loadable(lazy(() => import('pages/apps/order-item/list')));

const AppOwnMovementList = Loadable(lazy(() => import('pages/apps/own-movement/list')));
const AppOwnMovementCreate = Loadable(lazy(() => import('pages/apps/own-movement/create')));
const AppOwnMovementItemsList = Loadable(lazy(() => import('pages/apps/own-movement-item/list')));
const AppOwnMovementItemsCreate = Loadable(lazy(() => import('pages/apps/own-movement-item/create')));

const AppServiceOrderList = Loadable(lazy(() => import('pages/apps/service-order/list')));
const AppServiceOrderCreate = Loadable(lazy(() => import('pages/apps/service-order/create')));
const AppServiceOrderItemsList = Loadable(lazy(() => import('pages/apps/service-order-item/list')));
const AppServiceOrderItemsCreate = Loadable(lazy(() => import('pages/apps/service-order-item/create')));

const AppEquipmentPurchaseList = Loadable(lazy(() => import('pages/apps/equipment-purchase/list')));
const AppEquipmentPurchaseCreate = Loadable(lazy(() => import('pages/apps/equipment-purchase/create')));
const AppPurchaseItemsList = Loadable(lazy(() => import('pages/apps/purchase-item/list')));
const AppPurchaseItemsCreate = Loadable(lazy(() => import('pages/apps/purchase-item/create')));

const AppInventoryAdjustmentList = Loadable(lazy(() => import('pages/apps/inventory-adjustment/list')));
const AppInventoryAdjustmentCreate = Loadable(lazy(() => import('pages/apps/inventory-adjustment/create')));
const AppInventoryAdjustmentEdit = Loadable(lazy(() => import('pages/apps/inventory-adjustment/edit')));
const AppAdjustmentItemsList = Loadable(lazy(() => import('pages/apps/adjustment-item/list')));
const AppAdjustmentItemsCreate = Loadable(lazy(() => import('pages/apps/adjustment-item/create')));

const AppUserList = Loadable(lazy(() => import('pages/apps/user/list')));
const AppRoleList = Loadable(lazy(() => import('pages/apps/role/list')));
const AppPermissionList = Loadable(lazy(() => import('pages/apps/permission/list')));

// pages routing
const AuthLogin = Loadable(lazy(() => import('pages/auth/jwt/login')));
const AuthRegister = Loadable(lazy(() => import('pages/auth/jwt/register')));
const AuthForgotPassword = Loadable(lazy(() => import('pages/auth/jwt/forgot-password')));
const AuthResetPassword = Loadable(lazy(() => import('pages/auth/jwt/reset-password')));
const AuthCheckMail = Loadable(lazy(() => import('pages/auth/jwt/check-mail')));
const AuthCodeVerification = Loadable(lazy(() => import('pages/auth/jwt/code-verification')));

const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceTooManyRequests = Loadable(lazy(() => import('pages/maintenance/429')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const MaintenanceComingSoon = Loadable(lazy(() => import('pages/maintenance/coming-soon')));

const AppContactUS = Loadable(lazy(() => import('pages/contact-us')));

// render - sample page
const PricingPage = Loadable(lazy(() => import('pages/extra-pages/pricing')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: 'default',
              element: <DashboardDefault />
            },
            {
              path: 'analytics',
              element: <DashboardAnalytics />
            }
          ]
        },
        {
          path: 'widget',
          children: [
            {
              path: 'statistics',
              element: <WidgetStatistics />
            },
            {
              path: 'data',
              element: <WidgetData />
            },
            {
              path: 'chart',
              element: <WidgetChart />
            }
          ]
        },
        {
          path: 'apps',
          children: [
            {
              path: 'equipment',
              children: [
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['equipment.create']}>
                      <AppEquipmentCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'details/:id',
                  element: (
                    <PermissionGuard permissions={['equipment.read-detail']}>
                      <AppEquipmentDetails />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'edit/:id',
                  element: (
                    <PermissionGuard permissions={['equipment.modify']}>
                      <AppEquipmentEdit />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['equipment.read-all']}>
                      <AppEquipmentList />
                    </PermissionGuard>
                  )
                }
              ]
            },
            {
              path: 'equipment-order',
              children: [
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['order.create']}>
                      <AppEquipmentOrderCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'edit/:id',
                  element: (
                    <PermissionGuard permissions={['order.modify']}>
                      <AppEquipmentOrderEdit />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['order.read-all']}>
                      <AppEquipmentOrderList />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':orderId/items',
                  children: [
                    {
                      path: 'create',
                      element: (
                        <PermissionGuard permissions={['order-item.create']}>
                          <AppOrderItemsCreate />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'edit/:itemId',
                      element: (
                        <PermissionGuard permissions={['order-item.modify']}>
                          <AppOrderItemEdit />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['order-item.read-all']}>
                          <AppOrderItemsList />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'own-movement',
              children: [
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['own-movement.create']}>
                      <AppOwnMovementCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['own-movement.read-all']}>
                      <AppOwnMovementList />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':movementId/items',
                  children: [
                    {
                      path: 'create',
                      element: (
                        <PermissionGuard permissions={['own-movement-item.create']}>
                          <AppOwnMovementItemsCreate />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['own-movement-item.read-all']}>
                          <AppOwnMovementItemsList />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'service-order',
              children: [
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['service-order.read-all']}>
                      <AppServiceOrderList />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['service-order.create']}>
                      <AppServiceOrderCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':serviceOrderId/items',
                  children: [
                    {
                      path: 'create',
                      element: (
                        <PermissionGuard permissions={['service-order-item.create']}>
                          <AppServiceOrderItemsCreate />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['service-order-item.read-all']}>
                          <AppServiceOrderItemsList />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'equipment-purchase',
              children: [
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['purchase.create']}>
                      <AppEquipmentPurchaseCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['purchase.read-all']}>
                      <AppEquipmentPurchaseList />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':purchaseId/items',
                  children: [
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['purchase-item.read-all']}>
                          <AppPurchaseItemsList />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'create',
                      element: (
                        <PermissionGuard permissions={['purchase-item.create']}>
                          <AppPurchaseItemsCreate />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'inventory-adjustment',
              children: [
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['inventory-adjustment.read-all']}>
                      <AppInventoryAdjustmentList />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'create',
                  element: (
                    <PermissionGuard permissions={['inventory-adjustment.create']}>
                      <AppInventoryAdjustmentCreate />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'edit/:id',
                  element: (
                    <PermissionGuard permissions={['inventory-adjustment.modify']}>
                      <AppInventoryAdjustmentEdit />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':adjustmentId/items',
                  children: [
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['inventory-adjustment-item.read-all']}>
                          <AppAdjustmentItemsList />
                        </PermissionGuard>
                      )
                    },
                    {
                      path: 'create',
                      element: (
                        <PermissionGuard permissions={['inventory-adjustment-item.create']}>
                          <AppAdjustmentItemsCreate />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'warehouse',
              children: [
                {
                  path: 'admin-list',
                  element: (
                    <PermissionGuard permissions={['warehouse.read-admin']}>
                      <AppAdminWarehouseList />
                    </PermissionGuard>
                  )
                },
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['warehouse.read-all']}>
                      <AppWarehouseList />
                    </PermissionGuard>
                  )
                },
                {
                  path: ':warehouseId/own-inventory',
                  children: [
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['inventory.read-by-warehouse-id']}>
                          <AppOwnInventoryList />
                        </PermissionGuard>
                      )
                    }
                  ]
                },
                {
                  path: ':warehouseId/rented-inventory',
                  children: [
                    {
                      path: 'list',
                      element: (
                        <PermissionGuard permissions={['inventory.read-by-warehouse-id']}>
                          <AppRentedInventoryList />
                        </PermissionGuard>
                      )
                    }
                  ]
                }
              ]
            },
            {
              path: 'user',
              children: [
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['user.read-all']}>
                      <AppUserList />
                    </PermissionGuard>
                  )
                }
              ]
            },
            {
              path: 'role',
              children: [
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['role.read-all']}>
                      <AppRoleList />
                    </PermissionGuard>
                  )
                }
              ]
            },
            {
              path: 'permission',
              children: [
                {
                  path: 'list',
                  element: (
                    <PermissionGuard permissions={['permission.read-all']}>
                      <AppPermissionList />
                    </PermissionGuard>
                  )
                }
              ]
            },
            {
              path: 'profiles',
              children: [
                {
                  path: 'account',
                  element: <AccountProfile />,
                  children: [
                    {
                      path: 'basic',
                      element: <AccountTabProfile />
                    },
                    {
                      path: 'personal',
                      element: <AccountTabPersonal />
                    },
                    {
                      path: 'my-account',
                      element: <AccountTabAccount />
                    },
                    {
                      path: 'password',
                      element: <AccountTabPassword />
                    },
                    {
                      path: 'role',
                      element: <AccountTabRole />
                    },
                    {
                      path: 'settings',
                      element: <AccountTabSettings />
                    }
                  ]
                },
                {
                  path: 'user',
                  element: <UserProfile />,
                  children: [
                    {
                      path: 'personal',
                      element: <UserTabPersonal />
                    },
                    {
                      path: 'payment',
                      element: <UserTabPayment />
                    },
                    {
                      path: 'password',
                      element: <UserTabPassword />
                    },
                    {
                      path: 'settings',
                      element: <UserTabSettings />
                    }
                  ]
                }
              ]
            }
          ]
        },
        {
          path: 'pricing',
          element: <PricingPage />
        }
      ]
    },
    {
      path: '/maintenance',
      element: <PagesLayout />,
      children: [
        {
          path: '404',
          element: <MaintenanceError />
        },
        {
          path: '429',
          element: <MaintenanceTooManyRequests />
        },
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'coming-soon',
          element: <MaintenanceComingSoon />
        }
      ]
    },
    {
      path: '/auth',
      element: <PagesLayout />,
      children: [
        {
          path: 'login',
          element: <AuthLogin />
        },
        {
          path: 'register',
          element: <AuthRegister />
        },
        {
          path: 'forgot-password',
          element: <AuthForgotPassword />
        },
        {
          path: 'reset-password',
          element: <AuthResetPassword />
        },
        {
          path: 'check-mail',
          element: <AuthCheckMail />
        },
        {
          path: 'code-verification',
          element: <AuthCodeVerification />
        }
      ]
    },
    {
      path: '/',
      element: <SimpleLayout layout={SimpleLayoutType.SIMPLE} />,
      children: [
        {
          path: 'contact-us',
          element: <AppContactUS />
        }
      ]
    }
  ]
};

export default MainRoutes;
